import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Col, Row, Button, Card, ProgressBar } from 'react-bootstrap';
import PrefetchSessionSetup from '../../apollo/queries/PrefetchSessionSetup.graphql';
import { useHasSensitiveAccess } from '../session/useRoleBasedSessionQuery';
import { SessionType, AthleteContextLevel, GenderType } from '../../models';

// Mutations
import { UpdateSession } from '../../apollo/mutations/UpdateSession.graphql';
import CreateAthleteContext from '../../apollo/mutations/CreateAthleteContext.graphql';
import CreateRosterAthleteContext from '../../apollo/mutations/CreateRosterAthleteContext.graphql';
import { CreateSessionRoster } from '../../apollo/mutations/CreateSessionRoster.graphql';
import { UpdateSessionRoster } from '../../apollo/mutations/UpdateSessionRoster.graphql';
import { DeleteSessionRoster } from '../../apollo/mutations/DeleteSessionRoster.graphql';
import CreateRotation from '../../apollo/mutations/CreateRotation.graphql';
import DeleteRotation from '../../apollo/mutations/DeleteRotation.graphql';
import CreateStage from '../../apollo/mutations/CreateStage.graphql';
import UpdateStage from '../../apollo/mutations/UpdateStage.graphql';
import DeleteStage from '../../apollo/mutations/DeleteStage.graphql';
import CreateSquad from '../../apollo/mutations/CreateSquad.graphql';
import DeleteSquad from '../../apollo/mutations/DeleteSquad.graphql';
import UpdateSessionTeam from '../../apollo/mutations/UpdateSessionTeam.graphql';
import { UpdateLineup } from '../../apollo/mutations/UpdateLineup.graphql';

// Local imports
import {
  SessionSetupContext,
  useSortedSquads,
  useSortedRotations,
  useSortedSessionRosters,
  useSortedSessionTeams,
  useSortedLineups,
  useSessionConfiguration,
  useSetupProgress,
} from './hooks';
import {
  syncSessionInfo,
  syncRosters,
  syncRotations,
  diagnoseSession,
  repairCorruptedOrders,
  syncAll,
} from './sync';

import FullscreenLoading from '../helpers/fullscreenloading';
import Header from '../helpers/header';
import UserBubble from '../helpers/userbubble';
import MenuBubble from '../helpers/menububble';
import TeamTypeahead from '../helpers/teamtypeahead';
import { displayName } from '../../utilities/conversions';

import { getTeamTricode } from './utils';
import { SessionHeader } from './SessionHeader';
import { InfoRow } from './InfoRow';
import { TeamRow } from './TeamRow';
import { PendingChangesBanner } from './PendingChangesBanner';
import { RotationsSection } from './RotationsSection';
import { ConfigurePreview } from './ConfigurePreview';
import styles from './SessionSetup.module.css';

// ============================================================================
// Main Entry Point
// ============================================================================

export function SessionSetup() {
  const { sessionKey } = useParams();

  if (!sessionKey) {
    throw new Error('SessionSetup requires a :sessionKey URL parameter');
  }

  return <SessionSetupLoader sessionKey={sessionKey} />;
}

function SessionSetupLoader({ sessionKey }) {
  const hasSensitiveAccess = useHasSensitiveAccess();

  const { data, loading, error, refetch } = useQuery(PrefetchSessionSetup, {
    variables: { sessionKey },
    skip: !hasSensitiveAccess,
  });

  if (!hasSensitiveAccess) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Session setup requires administrator or judge privileges.</p>
      </div>
    );
  }

  if (loading) {
    return <FullscreenLoading message="Loading session setup..." />;
  }

  if (error) {
    console.error(error);
    return <div>Problem fetching session: {error.message}</div>;
  }

  if (!data?.SessionByKey?.items?.length) {
    return <div>No session found for {sessionKey}</div>;
  }

  const session = data.SessionByKey.items[0];

  return (
    <SessionSetupContext.Provider value={session}>
      <SessionSetupInner session={session} refetch={refetch} />
    </SessionSetupContext.Provider>
  );
}

// ============================================================================
// Main Setup Component
// ============================================================================

function SessionSetupInner({ session, refetch }) {
  const [log, setLog] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);

  // Configuration state
  const [type, setType] = useState(session.type || SessionType.DUAL);

  // Gender-aware defaults for tri sessions
  // Men: 3 squads (6 events ÷ 2 = 3), alternating
  // Women: 4 squads, non-alternating
  const isMale = session.gender === GenderType.MALE;
  const defaultSquadCount = isMale ? 3 : 4;
  const defaultAlternating = isMale ? true : false;

  // Use existing squad count, or team count, or gender-based default
  const existingSquadCount = session.squads?.items?.filter((s) => !s._deleted)
    ?.length;
  const teamCount =
    session.sessionTeams?.items?.filter((t) => !t._deleted)?.length || 0;
  const initialSquadCount =
    existingSquadCount > 0
      ? existingSquadCount
      : teamCount > defaultSquadCount
      ? teamCount
      : defaultSquadCount;

  const [alternating, setAlternating] = useState(
    session.alternating ?? defaultAlternating
  );
  const [squadCount, setSquadCount] = useState(initialSquadCount);
  const [byePositions, setByePositions] = useState(() => {
    // Detect bye positions from existing stage layout (R0)
    const rotations = session.rotations?.items
      ?.filter((r) => !r._deleted)
      ?.sort((a, b) => a.order - b.order);
    if (!rotations?.length) return isMale ? [7] : [2, 5];
    const stages = (rotations[0].stages?.items || []).filter(
      (s) => !s._deleted
    );
    const squads = session.squads?.items
      ?.filter((s) => !s._deleted)
      ?.sort((a, b) => a.name.localeCompare(b.name));
    if (!squads?.length || !stages.length) return isMale ? [7] : [2, 5];

    // Squad A (index 0) maps to events[0], Squad B to events[1], etc.
    const squadIdToIndex = {};
    squads.forEach((s, i) => {
      squadIdToIndex[s.id] = i;
    });
    const events = new Array(stages.length);
    for (const stage of stages) {
      const idx = squadIdToIndex[stage.squadId || stage.squad?.id];
      if (idx !== undefined) events[idx] = stage.apparatus;
    }

    // Find bye positions (1-indexed)
    const positions = [];
    events.forEach((apparatus, i) => {
      if (apparatus?.startsWith('BYE')) positions.push(i + 1);
    });
    return positions.length > 0 ? positions : isMale ? [7] : [2, 5];
  });
  const [autoAssignSquads, setAutoAssignSquads] = useState(true);
  const [autoUpdateTitle, setAutoUpdateTitle] = useState(true);

  // Sync squadCount on mount when we have more teams than default
  // (useState only captures initial value, won't update if session data changes)
  useEffect(() => {
    if (teamCount > defaultSquadCount) {
      setSquadCount(teamCount);
    } else if (existingSquadCount > 0) {
      setSquadCount(existingSquadCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Reset bye positions to defaults when user changes squad count or alternating
  const prevSquadCountRef = useRef(squadCount);
  const prevAlternatingRef = useRef(alternating);
  useEffect(() => {
    // Skip if nothing actually changed (initial mount)
    if (
      prevSquadCountRef.current === squadCount &&
      prevAlternatingRef.current === alternating
    )
      return;
    prevSquadCountRef.current = squadCount;
    prevAlternatingRef.current = alternating;

    if (alternating && squadCount === 3) {
      setByePositions([3, 5]);
    } else if (squadCount === 5) {
      setByePositions((prev) => [4, prev[1]]);
    } else if (squadCount === 6) {
      setByePositions([2, 5]);
    } else if (squadCount === 7) {
      setByePositions(isMale ? [7] : [2, 4, 6]);
    }
  }, [squadCount, alternating]);

  // Data hooks
  const progress = useSetupProgress();
  const sortedSessionTeams = useSortedSessionTeams();
  const sortedSessionRosters = useSortedSessionRosters();
  const sortedLineups = useSortedLineups();
  const sortedSquads = useSortedSquads();
  const sortedRotations = useSortedRotations();

  // Configuration calculator
  const {
    squads: desiredSquads,
    rotations: desiredRotations,
    events: desiredEvents,
    errorMessage,
  } = useSessionConfiguration({
    type,
    squadCount,
    alternating,
    gender: session.gender,
    byePositions,
  });

  // Mutations
  const [updateSession] = useMutation(UpdateSession);
  const [createSessionRoster] = useMutation(CreateSessionRoster);
  const [updateSessionRoster] = useMutation(UpdateSessionRoster);
  const [deleteSessionRoster] = useMutation(DeleteSessionRoster);
  const [createRotation] = useMutation(CreateRotation);
  const [deleteRotation] = useMutation(DeleteRotation);
  const [createStage] = useMutation(CreateStage);
  const [updateStage] = useMutation(UpdateStage);
  const [deleteStage] = useMutation(DeleteStage);
  const [createSquad] = useMutation(CreateSquad);
  const [deleteSquad] = useMutation(DeleteSquad);
  const [updateSessionTeam] = useMutation(UpdateSessionTeam);
  const [updateLineup] = useMutation(UpdateLineup);
  const [createAthleteContext] = useMutation(CreateAthleteContext);
  const [createRosterAthleteContext] = useMutation(CreateRosterAthleteContext);

  const mutations = {
    updateSession,
    createSessionRoster,
    updateSessionRoster,
    deleteSessionRoster,
    createRotation,
    deleteRotation,
    createStage,
    updateStage,
    deleteStage,
    createSquad,
    deleteSquad,
    updateSessionTeam,
    updateLineup,
    createAthleteContext,
    createRosterAthleteContext,
  };

  const addLog = useCallback((msg) => {
    const timestamp = new Date().toLocaleTimeString();
    setLog((prev) => [...prev, `[${timestamp}] ${msg}`]);
  }, []);

  // URL parameter for auto-configure on load
  const location = useLocation();
  const history = useHistory();
  const autoConfigTriggered = useRef(false);

  // Handle header save (called from SessionHeader component)
  const handleSaveHeader = async ({ name, startAt }) => {
    await mutations.updateSession({
      variables: {
        input: {
          id: session.id,
          _version: session._version,
          name,
          startAt,
        },
      },
    });
    await refetch();
  };

  // Handle Configure All - Phase 1: diagnose, show preview
  const handleConfigureAll = async () => {
    setDiagnosing(true);
    setDiagnosticResult(null);
    setLog([]);

    try {
      // Refetch to get the latest data
      const { data: freshData } = await refetch();
      const freshSession = freshData?.SessionByKey?.items?.[0];
      const freshSessionRosters =
        freshSession?.rosters?.items?.filter((sr) => !sr._deleted) || [];
      const freshRotations =
        freshSession?.rotations?.items
          ?.filter((r) => !r._deleted)
          ?.sort((a, b) => a.order - b.order) || [];
      const freshSquads =
        freshSession?.squads?.items?.filter((s) => !s._deleted) || [];
      const freshLineups =
        freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];
      const freshSessionTeams =
        freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];

      // Quick configure for unconfigured sessions (no rotations/squads yet)
      const isUnconfigured =
        freshRotations.length === 0 && freshSquads.length === 0;

      if (isUnconfigured) {
        addLog('Session not yet configured — running full auto-configure...');
        setDiagnosing(false);
        setSyncing(true);

        await syncAll(
          freshSession || session,
          {
            type,
            alternating,
            sessionTeams: freshSessionTeams,
            sessionRosters: freshSessionRosters,
            lineups: freshLineups,
            desiredSquads,
            desiredRotations,
            currentRotations: freshRotations,
            currentSquads: freshSquads,
            autoAssignSquads,
          },
          mutations,
          addLog,
          refetch
        );

        await refetch();
        setSyncing(false);
        return;
      }

      // Existing session — diagnose and show preview for selective repair
      addLog('Diagnosing session configuration...');

      const result = diagnoseSession({
        session: freshSession || session,
        sessionTeams: freshSessionTeams,
        sessionRosters: freshSessionRosters,
        lineups: freshLineups,
        desiredSquads,
        desiredRotations,
        currentRotations: freshRotations,
        currentSquads: freshSquads,
        autoAssignSquads,
      });

      setDiagnosticResult(result);
      addLog('Diagnosis complete. Review proposed changes below.');
    } catch (error) {
      addLog(`Error during diagnosis: ${error.message}`);
      console.error('Diagnosis error:', error);
    }

    setDiagnosing(false);
  };

  // Handle Configure All - Phase 2: apply selected fixes
  const handleApplySelected = async (selectedCategories) => {
    setSyncing(true);
    setDiagnosticResult(null);
    setLog([]);

    try {
      addLog('Applying selected fixes...');

      // Step 1: Fix orders if selected
      if (selectedCategories.fixOrders) {
        addLog('Fixing team and lineup orders...');

        // Refetch for fresh versions
        const { data: freshData } = await refetch();
        const freshSession = freshData?.SessionByKey?.items?.[0];
        const freshSessionTeams =
          freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];
        const freshLineups =
          freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];

        await repairCorruptedOrders(
          freshSessionTeams,
          freshLineups,
          {
            updateSessionTeam: mutations.updateSessionTeam,
            updateLineup: mutations.updateLineup,
          },
          addLog,
          selectedCategories.orderOverride
        );
      }

      // Step 2: Sync session info (always, it's cheap)
      addLog('Updating session info...');
      await syncSessionInfo(session, { type, alternating }, mutations);

      // Step 3: Sync rosters if selected
      if (selectedCategories.syncRosters) {
        addLog('Syncing rosters...');
        // Refetch for fresh data
        const { data: freshData } = await refetch();
        const freshSession = freshData?.SessionByKey?.items?.[0];
        const freshSessionTeams =
          freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];
        const freshSessionRosters =
          freshSession?.rosters?.items?.filter((sr) => !sr._deleted) || [];
        const freshLineups =
          freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];

        await syncRosters(
          freshSession || session,
          {
            sessionTeams: freshSessionTeams,
            sessionRosters: freshSessionRosters,
            lineups: freshLineups,
          },
          mutations,
          addLog
        );
      }

      // Step 4: Rebuild rotations if selected
      if (
        selectedCategories.rebuildRotations ||
        selectedCategories.autoAssignSquads
      ) {
        addLog('Syncing rotations...');

        // Refetch for fresh data
        const { data: freshData } = await refetch();
        const freshSession = freshData?.SessionByKey?.items?.[0];
        const freshSessionRosters =
          freshSession?.rosters?.items?.filter((sr) => !sr._deleted) || [];
        const freshRotations =
          freshSession?.rotations?.items
            ?.filter((r) => !r._deleted)
            ?.sort((a, b) => a.order - b.order) || [];
        const freshSquads =
          freshSession?.squads?.items?.filter((s) => !s._deleted) || [];
        const freshLineups =
          freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];
        const freshSessionTeams =
          freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];

        await syncRotations(
          freshSession || session,
          {
            desiredSquads,
            desiredRotations,
            currentRotations: freshRotations,
            currentSquads: freshSquads,
            sessionRosters: freshSessionRosters,
            sessionTeams: freshSessionTeams,
            lineups: freshLineups,
            autoAssignSquads: selectedCategories.autoAssignSquads,
            skipOrderRepair: true, // Already handled in step 1
          },
          mutations,
          addLog
        );
      }

      addLog('Done!');
    } catch (error) {
      addLog(`Error: ${error.message}`);
      console.error('Apply error:', error);
    }

    await refetch();
    setSyncing(false);
  };

  // Auto-configure on load if ?autoConfig=true is in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autoConfig = params.get('autoConfig') || params.get('autoConfigure');
    if (autoConfig === 'true' && !autoConfigTriggered.current && !syncing) {
      autoConfigTriggered.current = true;
      // Remove the param from URL so refresh doesn't re-trigger
      params.delete('autoConfig');
      params.delete('autoConfigure');
      const newSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
      // Trigger configure after a short delay to ensure component is ready
      setTimeout(() => {
        handleConfigureAll();
      }, 100);
    }
  }, [location.search, location.pathname, history, syncing]);

  return (
    <div className="main">
      <Header>
        <UserBubble />
        <MenuBubble />
      </Header>

      <div className={styles.container}>
        <SessionHeader session={session} onSave={handleSaveHeader} />

        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        <InfoRow
          session={session}
          type={type}
          setType={setType}
          squadCount={squadCount}
          setSquadCount={setSquadCount}
          alternating={alternating}
          setAlternating={setAlternating}
          byePositions={byePositions}
          setByePositions={setByePositions}
          autoAssignSquads={autoAssignSquads}
          setAutoAssignSquads={setAutoAssignSquads}
          autoUpdateTitle={autoUpdateTitle}
          setAutoUpdateTitle={setAutoUpdateTitle}
          progress={progress}
          syncing={syncing}
          diagnosing={diagnosing}
          errorMessage={errorMessage}
          onConfigureAll={handleConfigureAll}
        />

        {/* Configuration Preview Panel */}
        {diagnosticResult && (
          <ConfigurePreview
            diagnosis={diagnosticResult}
            onApply={handleApplySelected}
            onCancel={() => setDiagnosticResult(null)}
            applying={syncing}
          />
        )}

        {/* Two-column layout */}
        <Row>
          {/* Left: Teams, Rosters, Lineups */}
          <Col lg={6}>
            <Card className={styles.columnCard}>
              <Card.Header>
                <div className={styles.sectionHeader}>
                  <span>Teams, Rosters, and Lineups</span>
                  <div className={styles.progressSection}>
                    <div className={styles.progressLabel}>
                      <span>Progress</span>
                      <span>{progress.rosters}%</span>
                    </div>
                    <ProgressBar
                      now={progress.rosters}
                      variant={progress.rosters === 100 ? 'success' : 'primary'}
                      className={styles.progressBar}
                    />
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <RostersSection
                  session={session}
                  sessionRosters={sortedSessionRosters}
                  sessionTeams={sortedSessionTeams}
                  lineups={sortedLineups}
                  squads={sortedSquads}
                  desiredSquads={desiredSquads}
                  mutations={mutations}
                  refetch={refetch}
                  onLog={addLog}
                  autoUpdateTitle={autoUpdateTitle}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Right: Rotations, Stages, Squads */}
          <Col lg={6}>
            <Card className={styles.columnCard}>
              <Card.Header>
                <div className={styles.sectionHeader}>
                  <span>Rotations, Stages, and Squads</span>
                  <div className={styles.progressSection}>
                    <div className={styles.progressLabel}>
                      <span>Progress</span>
                      <span>{progress.rotations}%</span>
                    </div>
                    <ProgressBar
                      now={progress.rotations}
                      variant={
                        progress.rotations === 100 ? 'success' : 'primary'
                      }
                      className={styles.progressBar}
                    />
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <RotationsSection
                  desiredSquads={desiredSquads}
                  desiredRotations={desiredRotations}
                  desiredEvents={desiredEvents}
                  currentSquads={sortedSquads}
                  currentRotations={sortedRotations}
                  sessionRosters={sortedSessionRosters}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Log Output */}
        {log.length > 0 && (
          <div className={styles.logSection}>
            <h5>Activity Log</h5>
            <div className={styles.logOutput}>
              {log.map((entry, i) => (
                <div key={i}>{entry}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Rosters Section
// ============================================================================

// Helper to check if a roster needs conversion from legacy RosterLinks to AthleteContexts
function getRosterLegacyStatus(roster) {
  if (!roster)
    return { hasLegacy: false, hasNew: false, needsConversion: false };

  const legacyAthletes = (roster.athletes?.items || []).filter(
    (rl) => !rl._deleted && rl.active
  );
  const newAthletes = (roster.athleteContexts?.items || []).filter(
    (rac) => !rac._deleted
  );

  return {
    hasLegacy: legacyAthletes.length > 0,
    hasNew: newAthletes.length > 0,
    needsConversion: legacyAthletes.length > 0 && newAthletes.length === 0,
    legacyCount: legacyAthletes.length,
    newCount: newAthletes.length,
    legacyAthletes, // Include the actual athlete data
  };
}

// Component to display legacy data warning with team names and athlete list
function LegacyDataWarning({
  rostersNeedingConversion,
  converting,
  conversionProgress,
  onConvertAll,
}) {
  const [expanded, setExpanded] = useState(false);

  // Gather data about each roster needing conversion
  const rosterDetails = rostersNeedingConversion.map((sr) => {
    const roster = sr.roster;
    const teamName = roster?.team?.name || 'Unknown Team';
    const tricode =
      roster?.team?.triCode || getTeamTricode(roster?.team?.name) || '???';
    const legacyAthletes = (roster?.athletes?.items || []).filter(
      (rl) => !rl._deleted && rl.active
    );
    return {
      teamName,
      tricode,
      athletes: legacyAthletes.map((rl) => ({
        name: rl.athlete?.name || 'Unknown',
        classYear: rl.classYear,
      })),
    };
  });

  const totalAthletes = rosterDetails.reduce(
    (sum, r) => sum + r.athletes.length,
    0
  );
  const teamNames = rosterDetails.map((r) => r.tricode).join(', ');

  // Calculate progress percentage
  const progressPercent = conversionProgress
    ? Math.round((conversionProgress.current / conversionProgress.total) * 100)
    : 0;

  return (
    <div className={styles.legacyWarning}>
      <div className={styles.legacyWarningHeader}>
        <span className={styles.legacyWarningIcon}>⚠️</span>
        <span className={styles.legacyWarningText}>
          <strong>{teamNames}</strong>
          {rostersNeedingConversion.length > 1
            ? ' have legacy athlete data'
            : ' has legacy athlete data'}{' '}
          ({totalAthletes} athlete{totalAthletes !== 1 ? 's' : ''})
        </span>
        {!converting && (
          <Button
            variant="link"
            size="sm"
            className={styles.legacyExpandBtn}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Hide' : 'Show'} athletes
          </Button>
        )}
        <Button
          variant="warning"
          size="sm"
          onClick={onConvertAll}
          disabled={converting}
        >
          {converting ? 'Converting...' : 'Convert All'}
        </Button>
      </div>

      {/* Progress bar during conversion */}
      {converting && conversionProgress && (
        <div className={styles.legacyProgressSection}>
          <div className={styles.legacyProgressInfo}>
            <span>
              Converting: <strong>{conversionProgress.currentAthlete}</strong>
            </span>
            <span>
              {conversionProgress.current} / {conversionProgress.total}
            </span>
          </div>
          <ProgressBar
            now={progressPercent}
            variant="warning"
            className={styles.legacyProgressBar}
            animated
          />
        </div>
      )}

      {expanded && !converting && (
        <div className={styles.legacyAthleteList}>
          {rosterDetails.map((roster, idx) => (
            <div key={idx} className={styles.legacyTeamSection}>
              <div className={styles.legacyTeamName}>
                <span className={styles.teamTricode}>{roster.tricode}</span>
                <span>{roster.teamName}</span>
                <span className={styles.legacyAthleteCount}>
                  ({roster.athletes.length} athlete
                  {roster.athletes.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className={styles.legacyAthleteGrid}>
                {roster.athletes.map((athlete, aIdx) => (
                  <span key={aIdx} className={styles.legacyAthleteName}>
                    {athlete.name}
                    {athlete.classYear && (
                      <span className={styles.legacyClassYear}>
                        {' '}
                        ({athlete.classYear})
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RostersSection({
  session,
  sessionRosters,
  sessionTeams,
  lineups,
  squads,
  desiredSquads,
  mutations,
  refetch,
  onLog,
  autoUpdateTitle,
}) {
  const [syncing, setSyncing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [converting, setConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(null);

  // Pending squad assignments: { rosterId: squadName }
  const [pendingSquads, setPendingSquads] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const debounceRef = useRef(null);

  // Check for any rosters needing conversion
  const rostersNeedingConversion = sessionRosters.filter(
    (sr) => getRosterLegacyStatus(sr.roster).needsConversion
  );

  // Convert all rosters that need conversion with progress tracking
  const handleConvertAll = async () => {
    setConverting(true);

    // Calculate total athletes across all rosters
    const allAthletes = [];
    for (const sr of rostersNeedingConversion) {
      const roster = sr.roster;
      if (!roster) continue;
      const legacyAthletes = (roster.athletes?.items || []).filter(
        (rl) => !rl._deleted && rl.active
      );
      for (const rl of legacyAthletes) {
        allAthletes.push({
          sessionRoster: sr,
          roster,
          rosterLink: rl,
          athlete: rl.athlete,
        });
      }
    }

    const total = allAthletes.length;
    let current = 0;

    setConversionProgress({ current: 0, total, currentAthlete: 'Starting...' });
    onLog?.(`Converting ${total} athletes from legacy format...`);

    try {
      for (const item of allAthletes) {
        const { roster, rosterLink, athlete } = item;
        if (!athlete) {
          current++;
          continue;
        }

        // Update progress
        setConversionProgress({
          current,
          total,
          currentAthlete: athlete.name || 'Unknown',
        });

        // Find existing AthleteContext that matches level and division
        const level = AthleteContextLevel.COLLEGE;
        const division = rosterLink.classYear
          ? `C_${rosterLink.classYear}`
          : null;

        let matchingContext = (athlete.contexts?.items || []).find(
          (ctx) =>
            !ctx._deleted &&
            ctx.level === level &&
            (!division || ctx.division === division)
        );

        // Create AthleteContext if none exists
        if (!matchingContext) {
          const input = {
            athleteId: athlete.id,
            level,
          };
          if (division) {
            input.division = division;
          }
          const { data } = await mutations.createAthleteContext({
            variables: { input },
          });
          matchingContext = data.createAthleteContext;
          onLog?.(`  Created context for ${athlete.name}`);
        }

        // Check if roster already has this context linked
        const existingLink = (roster.athleteContexts?.items || []).find(
          (rac) =>
            !rac._deleted && rac.athleteContext?.id === matchingContext.id
        );

        // Create RosterAthleteContext if not linked
        if (!existingLink) {
          await mutations.createRosterAthleteContext({
            variables: {
              input: {
                rosterId: roster.id,
                athleteContextId: matchingContext.id,
              },
            },
          });
          onLog?.(`  Linked ${athlete.name} to roster`);
        }

        current++;
        setConversionProgress({
          current,
          total,
          currentAthlete: athlete.name || 'Unknown',
        });
      }

      onLog?.(`Conversion complete! Converted ${total} athletes.`);
      await refetch();
    } catch (error) {
      console.error('Conversion error:', error);
      onLog?.(`Error: ${error.message}`);
    } finally {
      setConverting(false);
      setConversionProgress(null);
    }
  };

  // Clear pending state when data refreshes
  useEffect(() => {
    if (Object.keys(pendingSquads).length > 0 && !showConfirm) {
      // Data refreshed, clear pending if not showing confirm
    }
  }, [sessionRosters, pendingSquads, showConfirm]);

  // Build a map of teamId -> sessionRoster for quick lookup
  // Track which session rosters are matched to a sessionTeam
  const rosterByTeamId = {};
  const matchedRosterIds = new Set();
  const sessionTeamTeamIds = new Set(
    sessionTeams.map((st) => st.team?.id).filter(Boolean)
  );
  for (const sr of sessionRosters) {
    const teamId = sr.roster?.teamId || sr.roster?.team?.id;
    if (teamId && sessionTeamTeamIds.has(teamId) && !rosterByTeamId[teamId]) {
      rosterByTeamId[teamId] = sr;
      matchedRosterIds.add(sr.id);
    }
  }
  // Extra rosters not matched to any sessionTeam row (e.g. individuals)
  const extraSessionRosters = sessionRosters.filter(
    (sr) => !matchedRosterIds.has(sr.id)
  );

  // Build a map of squadName -> squad for quick lookup
  const squadByName = {};
  for (const s of squads) {
    squadByName[s.name] = s;
  }

  // Check if there are pending changes
  const hasPendingChanges = Object.keys(pendingSquads).length > 0;

  const handleSync = async () => {
    setSyncing(true);
    await syncRosters(
      session,
      { sessionTeams, sessionRosters, lineups },
      mutations,
      onLog
    );
    await refetch();
    setSyncing(false);
  };

  const handleDeleteRoster = async (sr) => {
    await mutations.deleteSessionRoster({
      variables: {
        input: {
          id: sr.id,
          _version: sr._version,
        },
      },
    });
    await refetch();
  };

  const handleRosterChange = async (sr, team, newRosterId) => {
    if (!newRosterId) {
      // Delete the session roster
      await handleDeleteRoster(sr);
    } else if (sr) {
      // Need to delete and recreate since rosterId isn't updatable
      await mutations.deleteSessionRoster({
        variables: {
          input: { id: sr.id, _version: sr._version },
        },
      });
      await mutations.createSessionRoster({
        variables: {
          input: {
            sessionId: session.id,
            rosterId: newRosterId,
            lineupId: sr.lineupId,
            squadId: sr.squadId,
          },
        },
      });
      await refetch();
    } else {
      // Create new session roster
      const lineup = lineups.find((l) => l.teamId === team.id);
      await mutations.createSessionRoster({
        variables: {
          input: {
            sessionId: session.id,
            rosterId: newRosterId,
            lineupId: lineup?.id,
          },
        },
      });
      await refetch();
    }
  };

  const handleLineupChange = async (sr, lineupId) => {
    if (!sr || applying) return; // Prevent changes while applying
    try {
      await mutations.updateSessionRoster({
        variables: {
          input: {
            id: sr.id,
            _version: sr._version,
            lineupId: lineupId || null,
          },
        },
      });
      await refetch();
    } catch (error) {
      console.error('Error updating lineup:', error);
    }
  };

  // Click squad button - update pending state and debounce
  const handleSquadClick = (sr, squadName) => {
    if (!sr || applying) return;

    // Get current squad name for this roster
    const currentSquad = squads.find((s) => s.id === sr.squadId);
    const currentSquadName = currentSquad?.name;

    // Skip if clicking the same squad that's already assigned
    if (currentSquadName === squadName && !pendingSquads[sr.id]) return;

    // If clicking current squad while there's a pending change, remove the pending change
    if (currentSquadName === squadName && pendingSquads[sr.id]) {
      setPendingSquads((prev) => {
        const next = { ...prev };
        delete next[sr.id];
        return next;
      });
      return;
    }

    // Update pending state
    setPendingSquads((prev) => ({
      ...prev,
      [sr.id]: squadName,
    }));

    // Reset debounce timer
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setShowConfirm(true);
    }, 1500); // 1.5 second debounce
  };

  // Cancel pending changes
  const handleCancelChanges = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPendingSquads({});
    setShowConfirm(false);
  };

  // Apply all pending changes in batch
  const handleApplyChanges = async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setApplying(true);
    setShowConfirm(false);

    try {
      // Get fresh data first
      const { data: freshData } = await refetch();
      const freshSession = freshData?.SessionByKey?.items?.[0];
      const freshRosters =
        freshSession?.rosters?.items?.filter((sr) => !sr._deleted) || [];
      const freshSessionTeams =
        freshSession?.sessionTeams?.items?.filter((st) => !st._deleted) || [];
      const freshLineups =
        freshSession?.lineups?.items?.filter((l) => !l._deleted) || [];

      // Build roster updates with fresh versions
      const rosterUpdates = [];
      for (const [rosterId, squadName] of Object.entries(pendingSquads)) {
        const freshRoster = freshRosters.find((r) => r.id === rosterId);
        if (freshRoster) {
          const squad = squadByName[squadName];
          rosterUpdates.push({
            roster: freshRoster,
            squadId: squad?.id || null,
            squadName,
            teamId: freshRoster.roster?.teamId || freshRoster.roster?.team?.id,
          });
        }
      }

      // Step 1: Apply squad assignments
      onLog?.('Updating squad assignments...');
      for (const update of rosterUpdates) {
        await mutations.updateSessionRoster({
          variables: {
            input: {
              id: update.roster.id,
              _version: update.roster._version,
              squadId: update.squadId,
            },
          },
        });
        onLog?.(
          `  ${
            update.roster.roster?.team?.triCode ||
            getTeamTricode(update.roster.roster?.team?.name)
          } → Squad ${update.squadName}`
        );
      }

      // Step 2: Calculate new team order based on squad assignments
      // Use sequential ordering (0, 1, 2, ...) based on sorted squad names
      // This handles cases like squads A, B, D being assigned (should be orders 0, 1, 2, not 0, 1, 3)

      // Build a complete picture of ALL teams' final squad assignments (existing + pending changes)
      const allTeamSquads = [];
      for (const sr of freshRosters) {
        const teamId = sr.roster?.teamId || sr.roster?.team?.id;
        // Check if there's a pending change for this roster
        const pendingSquadName = pendingSquads[sr.id];
        // Get existing squad name
        const existingSquad = squads.find((s) => s.id === sr.squadId);
        const existingSquadName = existingSquad?.name;
        // Use pending if exists, otherwise existing
        const finalSquadName = pendingSquadName || existingSquadName;
        if (teamId && finalSquadName) {
          allTeamSquads.push({ teamId, squadName: finalSquadName });
        }
      }

      // Get unique squad names sorted alphabetically, then create sequential mapping
      const usedSquads = [
        ...new Set(allTeamSquads.map((t) => t.squadName)),
      ].sort();
      const squadToOrder = Object.fromEntries(
        usedSquads.map((squad, idx) => [squad, idx])
      );

      // Build a map of teamId -> new order based on squad assignment
      const teamOrderMap = {};
      for (const { teamId, squadName } of allTeamSquads) {
        teamOrderMap[teamId] = squadToOrder[squadName];
      }

      // Step 3: Update SessionTeam orders
      onLog?.('Updating team order...');
      for (const st of freshSessionTeams) {
        const newOrder = teamOrderMap[st.teamId];
        if (newOrder !== undefined && newOrder !== st.order) {
          await mutations.updateSessionTeam({
            variables: {
              input: {
                id: st.id,
                _version: st._version,
                order: newOrder,
              },
            },
          });
          onLog?.(
            `  ${st.team?.triCode || getTeamTricode(st.team?.name)} order: ${
              st.order
            } → ${newOrder}`
          );
        }
      }

      // Step 4: Update Lineup orders to match team orders
      onLog?.('Updating lineup order...');
      for (const lineup of freshLineups) {
        const newOrder = teamOrderMap[lineup.teamId];
        if (newOrder !== undefined && newOrder !== lineup.order) {
          await mutations.updateLineup({
            variables: {
              input: {
                id: lineup.id,
                _version: lineup._version,
                order: newOrder,
              },
            },
          });
          // Find the team for this lineup to show tricode
          const lineupTeam = freshSessionTeams.find(
            (st) => st.teamId === lineup.teamId
          );
          const lineupTricode =
            lineupTeam?.team?.triCode || getTeamTricode(lineupTeam?.team?.name);
          onLog?.(
            `  ${lineupTricode} lineup order: ${
              lineup.order ?? '?'
            } → ${newOrder}`
          );
        }
      }

      // Step 5: Update session title based on new team order
      onLog?.('Updating session title...');
      const sortedTeams = [...freshSessionTeams]
        .map((st) => ({
          ...st,
          newOrder: teamOrderMap[st.teamId] ?? st.order,
        }))
        .sort((a, b) => a.newOrder - b.newOrder);

      // Update title if enabled
      if (autoUpdateTitle) {
        // Use correct separator based on session type:
        // DUAL: "Team A vs Team B"
        // MULTI: "Team A / Team B / Team C / ..."
        // Use displayName to get short name from altNames (e.g., #Oklahoma -> Oklahoma)
        const separator = session.type === 'DUAL' ? ' vs ' : ' / ';
        const newTitle = sortedTeams
          .map(
            (st) => displayName(st.team?.name, st.team?.altNames) || 'Unknown'
          )
          .join(separator);

        if (newTitle && newTitle !== freshSession.name) {
          await mutations.updateSession({
            variables: {
              input: {
                id: freshSession.id,
                _version: freshSession._version,
                name: newTitle,
              },
            },
          });
          onLog?.(`  Title: "${newTitle}"`);
        }
      }

      onLog?.('All changes applied!');
      setPendingSquads({});
      await refetch();
    } catch (error) {
      console.error('Error applying changes:', error);
      onLog?.(`Error: ${error.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      {/* Legacy data warning banner */}
      {rostersNeedingConversion.length > 0 && (
        <LegacyDataWarning
          rostersNeedingConversion={rostersNeedingConversion}
          converting={converting}
          conversionProgress={conversionProgress}
          onConvertAll={handleConvertAll}
        />
      )}

      {/* Compact add row */}
      <div className={styles.addTeamRow}>
        <TeamTypeahead
          gender={session.gender}
          prompt="Add team..."
          onSelected={async (team) => {
            const teamRosters = team.rosters?.items || [];
            if (teamRosters.length > 0) {
              const lineup = lineups.find((l) => l.teamId === team.id);
              await mutations.createSessionRoster({
                variables: {
                  input: {
                    sessionId: session.id,
                    rosterId: teamRosters[0].id,
                    lineupId: lineup?.id,
                  },
                },
              });
              await refetch();
            }
          }}
        />
        <Button
          variant="outline-secondary"
          disabled={syncing}
          onClick={handleSync}
          title="Auto-link rosters and lineups for all teams"
        >
          {syncing ? '...' : 'Auto-Link'}
        </Button>
      </div>

      {/* Column headers */}
      {sessionTeams.length > 0 && (
        <div className={styles.teamHeaderRow}>
          <span className={styles.teamHeaderOrder}>#</span>
          <span className={styles.teamHeaderSquad}>Squad</span>
          <span className={styles.teamHeaderTeam}>Team</span>
          <span className={styles.teamHeaderRoster}>Roster</span>
          <span className={styles.teamHeaderLineup}>Lineup</span>
          <span className={styles.teamHeaderActions}></span>
        </div>
      )}

      {/* Team list - show teams from sessionTeams sorted by order */}
      <div className={styles.rosterList}>
        {(() => {
          // Detect duplicate sessionTeam orders
          const orderCounts = {};
          for (const st of sessionTeams) {
            orderCounts[st.order] = (orderCounts[st.order] || 0) + 1;
          }

          return sessionTeams.map((st) => {
            const team = st.team;
            const sr = rosterByTeamId[team?.id];
            const squad = sr ? squads.find((s) => s.id === sr.squadId) : null;
            const hasDuplicateOrder = orderCounts[st.order] > 1;

            return (
              <TeamRow
                key={st.id}
                sessionTeam={st}
                sessionRoster={sr}
                squad={squad}
                desiredSquads={desiredSquads}
                pendingSquads={pendingSquads}
                applying={applying}
                lineups={lineups}
                hasDuplicateOrder={hasDuplicateOrder}
                onSquadClick={handleSquadClick}
                onRosterChange={handleRosterChange}
                onLineupChange={handleLineupChange}
                onDelete={handleDeleteRoster}
              />
            );
          });
        })()}

        {/* Extra session rosters not matched to a sessionTeam (e.g. individuals) */}
        {extraSessionRosters.map((sr) => {
          const team = sr.roster?.team;
          const lineup = sr.lineupId
            ? lineups.find((l) => l.id === sr.lineupId)
            : null;
          const squad = sr.squadId
            ? squads.find((s) => s.id === sr.squadId)
            : null;
          const teamName = displayName(team?.name, team?.altNames) || 'Unknown';
          const tricode = team?.triCode || getTeamTricode(team?.name);
          const lineupLabel = lineup
            ? `${lineup.title || 'Lineup'}${
                lineup.individuals ? ' (indv)' : ''
              }`
            : 'No lineup';

          return (
            <div
              key={sr.id}
              className={styles.teamRow}
              style={{ opacity: 0.85 }}
            >
              <span className={styles.teamOrder} style={{ color: '#6b7280' }}>
                —
              </span>
              <div className={styles.squadButtons}>
                {desiredSquads.map((squadName) => {
                  const pendingSquad = pendingSquads[sr.id];
                  const currentSquad = squad?.name;
                  const isPending = pendingSquad === squadName;
                  const isCurrent = currentSquad === squadName && !pendingSquad;
                  return (
                    <button
                      key={squadName}
                      className={`${styles.squadBtn} ${
                        isCurrent ? styles.squadBtnActive : ''
                      } ${isPending ? styles.squadBtnPending : ''}`}
                      onClick={() => handleSquadClick(sr, squadName)}
                      disabled={applying}
                      title={`Assign to Squad ${squadName}`}
                    >
                      {squadName}
                    </button>
                  );
                })}
              </div>
              <span className={styles.teamName}>
                <span
                  className={styles.teamChip}
                  style={{ backgroundColor: '#9ca3af' }}
                >
                  {tricode}
                </span>
                <span className={styles.teamFullName}>{teamName}</span>
              </span>
              <span
                className={styles.teamSelect}
                style={{ fontSize: '0.75rem', color: '#6b7280' }}
              >
                {sr.roster?.title || 'Roster'}
              </span>
              <span
                className={styles.teamSelect}
                style={{ fontSize: '0.75rem', color: '#6b7280' }}
              >
                {lineupLabel}
              </span>
              <button
                className={styles.teamRemove}
                onClick={() => handleDeleteRoster(sr)}
                title="Remove from session"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {sessionTeams.length === 0 && extraSessionRosters.length === 0 && (
        <div className="text-muted small py-2">No teams added yet.</div>
      )}

      {/* Pending changes confirmation */}
      {(hasPendingChanges || showConfirm) && (
        <PendingChangesBanner
          sessionTeams={sessionTeams}
          sessionRosters={sessionRosters}
          squads={squads}
          pendingSquads={pendingSquads}
          applying={applying}
          onApply={handleApplyChanges}
          onCancel={handleCancelChanges}
        />
      )}
    </div>
  );
}

export default SessionSetup;
